import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCameraReturn {
  stream: MediaStream | null;
  error: string | null;
  isActive: boolean;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  captureScreenshot: () => string | null;
}

export function useCamera(): UseCameraReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    setIsActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (isActive && streamRef.current) return;
    setError(null);
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = newStream;
      setStream(newStream);
      setIsActive(true);
    } catch (err: any) {
      console.error('Error accessing camera:', err);
      let msg = 'Could not access camera.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Camera permission denied. Please enable camera access in your browser settings.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        msg = 'No camera found on this device.';
      }
      setError(msg);
      setIsActive(false);
    }
  }, []);

  const captureScreenshot = useCallback(() => {
    if (!streamRef.current) return null;
    const videoTracks = streamRef.current.getVideoTracks();
    if (videoTracks.length === 0) return null;

    // Create a temporary video element to play the stream if needed,
    // but since we usually have one playing in the UI, we can just use the stream.
    // However, it's more reliable to create a temporary video element
    // or use a reference to the active one.
    // For simplicity and to avoid dependency on a specific video element,
    // we'll try to find any active video element with this stream or create a ghost one.

    // A better way: create a ghost video element, wait for it to be ready, then draw.
    // But capture needs to be synchronous or return a promise.
    // Given the context, we likely have a <video> element active in the UI.

    const video = document.querySelector('video[aria-label="Camera preview"]') as HTMLVideoElement;
    if (!video || !video.videoWidth) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Flip horizontally for user-facing camera
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg', 0.8);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    stream,
    error,
    isActive,
    startCamera,
    stopCamera,
    captureScreenshot,
  };
}

export default useCamera;
